<template>
  <div class="bg-white pb-24 sm:pb-32">
    <div class="mx-auto mt-40 max-w-7xl px-6 lg:px-8">
      <div class="mx-auto lg:mx-0">
        <h2 class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">{{ content.pageTitle }}</h2>
        <div v-if="introductionHtml" v-html="introductionHtml" class="mt-6 mb-6 text-lg leading-8 text-gray-600 rich-text-content"></div>
      </div>
      <div class="mx-auto lg:mx-0">
        <h2 class="text-3xl mb-6 font-bold tracking-tight text-gray-900 sm:text-4xl">{{ content.constitution }}</h2>
        <a :href="content.constitutionLink" target="_blank">
            <button type="button" class="rounded-md bg-custom-blue mb-8 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:custom-blue-pale focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custom-blue">
                PVIS Constitution
            </button>
          </a>
          
      </div>
      <div class="mx-auto mb-10 lg:mx-0">
        <div class="rounded-lg border border-custom-blue/20 bg-blue-50 p-6">
          <h2 class="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">Board Nominations</h2>
          <p class="mt-4 text-lg leading-8 text-gray-600">
            Registrations for board nominations close at 2 pm, Thursday 3rd September.
          </p>
          <a
            href="https://form.jotform.com/261958234734061"
            target="_blank"
            rel="noopener noreferrer"
            class="mt-5 inline-flex rounded-md bg-custom-blue px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-custom-blue-pale focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custom-blue"
          >
            Complete the Board Nomination Form
          </a>
        </div>
      </div>
      <div class="mx-auto lg:mx-0">
        <h2 class="text-3xl mb-6 font-bold tracking-tight text-gray-900 sm:text-4xl">Contact</h2>
        <p class="text-lg leading-8 text-gray-600">To communicate directly with the Board via the Board Chair please email <a href="mailto:boardchair@pvis.edu.vu" class="text-blue-500 hover:underline">boardchair@pvis.edu.vu</a></p>
      </div>
<!-- Board Members -->
 <div class="mx-auto lg:mx-0">
          <h2 class="text-3xl mt-6 font-bold tracking-tight text-gray-900 sm:text-4xl">{{ content.ourMembersTitle }}</h2>
        </div>
        <div class="mx-auto mt-6 max-w-4xl">
          <div v-for="member in content.members" :key="member.sys.id" class="text-center mb-8">
            <h3 class="text-xl font-semibold leading-8 tracking-tight text-gray-900">{{ member.fields.name }}</h3>
            <p class="text-lg leading-7 text-gray-600 mt-2">{{ member.fields.title }}</p>
            <div v-if="member.fields.email" class="text-base leading-7 text-gray-600 mt-2">
              <a :href="'mailto:' + member.fields.email" class="text-blue-500 hover:underline">
                {{ member.fields.email }}
              </a>
            </div>
          </div>
        </div> 

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';

const content = ref({})
const loading = ref(true);

const introductionHtml = computed(() => {
  if (content.value && content.value.introduction && content.value.introduction.nodeType === 'document') {
    return documentToHtmlString(content.value.introduction);
  }
  return '';
});

const fetchContent = async () => {
  // Directly use the VUE_APP_ prefixed variable, which Vue CLI makes available
  const accessToken = process.env.VUE_APP_CONTENTFUL_ACCESS_TOKEN;

  if (!accessToken) {
    console.error('Contentful access token is missing for board page. Please check your environment variables.');
    content.value = { pageTitle: 'Our Board', introduction: 'Configuration error', members: [] }; // Default content on error
    loading.value = false;
    return; // Stop execution if the token is missing
  }

  try {
    const response = await axios.get(`https://cdn.contentful.com/spaces/8ayqhzh3p1ua/environments/master/entries?access_token=${accessToken}&content_type=board`)
    console.log('Fetched content:', response.data);
    if (response.data.items && response.data.items.length > 0) {
      const fields = response.data.items[0].fields;
      const assets = response.data.includes.Asset;
      
      content.value = {
        pageTitle: fields.pageTitle,
        introduction: fields.introduction,
        constitution: fields.constitution,
        constitutionLink: fields.constitutionLink,
        ourMembersTitle: fields.ourMembersTitle,
        members: fields.members.map(member => {
          const memberData = response.data.includes.Entry.find(entry => entry.sys.id === member.sys.id);
          if (memberData) {
            const photoAsset = assets.find(asset => asset.sys.id === memberData.fields.photo.sys.id);
            return {
              ...memberData,
              fields: {
                ...memberData.fields,
                photoUrl: photoAsset ? `https:${photoAsset.fields.file.url}` : null
              }
            };
          }
          return member;
        })
      };
    } else {
      console.error('No content found');
      content.value = { pageTitle: 'Our Board', introduction: 'Content not available', members: [] };
    }
  } catch (error) {
    console.error('Error fetching content from Contentful:', error);
    content.value = { pageTitle: 'Our Board', introduction: 'Error loading content', members: [] };
  } finally {
    loading.value = false;
  }
};



onMounted(() => {
  fetchContent();
});



</script>

<style scoped>
/* Optional: Add specific styles for rich text content if needed */
.rich-text-content ::v-deep p {
  margin-bottom: 1em; /* Example: Add bottom margin to paragraphs */
}
.rich-text-content ::v-deep a {
  color: theme('colors.custom-blue');
  text-decoration: underline;
}
/* Add more specific styles for other rich text elements like headings, lists, etc. */
</style>
